import {
  BlotterTable,
  BlotterTableFilters,
  Box,
  Button,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  Text,
  Tooltip,
  useBlotterObservables,
  useBlotterTable,
} from '@talos/kyoko';
import type { RowSelectionOptions } from 'ag-grid-community';
import { useMemo } from 'react';
import type { SubAccountWarning } from './nettingSubAccount';
import type { SelectableCareOrder } from './SelectableCareOrder';
import { useCareOrderNettingPreviewColumns } from './useCareOrderNettingPreviewColumns';
import { useCareOrderNettingPreviewSelection } from './useCareOrderNettingPreviewSelection';
import type { CareOrderSelectionChange } from './useSelectableCareOrders';

const memberCareOrdersRowSelection = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  selectAll: 'filtered',
  enableClickSelection: true,
  enableSelectionWithoutKeys: true,
  copySelectedRows: true,
} as const satisfies RowSelectionOptions<SelectableCareOrder>;

const readOnlyMemberCareOrdersRowSelection = {
  mode: 'multiRow',
  checkboxes: () => false,
  headerCheckbox: false,
  hideDisabledCheckboxes: true,
  enableClickSelection: false,
  isRowSelectable: () => false,
  copySelectedRows: true,
} as const satisfies RowSelectionOptions<SelectableCareOrder>;

interface MemberCareOrdersBlotterProps {
  portalId?: string;
  showAllDecimals: boolean;
  memberCareOrders: SelectableCareOrder[];
  onSelectionChanged: (selectionChanges: CareOrderSelectionChange[]) => void;
  onShowCompatibleCareOrders: () => void;
  showCompatibleCareOrders: boolean;
  readOnly?: boolean;
  subAccountWarning?: SubAccountWarning;
}

export function MemberCareOrdersBlotter({
  portalId,
  showAllDecimals,
  memberCareOrders,
  onSelectionChanged,
  onShowCompatibleCareOrders,
  showCompatibleCareOrders,
  readOnly = false,
  subAccountWarning,
}: MemberCareOrdersBlotterProps) {
  const {
    selectedRows,
    updateCareOrderSelection: removeCareOrder,
    updateSelectedCareOrdersSelection: removeSelectedCareOrders,
    handleSelectionChanged,
  } = useCareOrderNettingPreviewSelection({
    action: 'remove',
    onSelectionChanged,
  });

  const columns = useCareOrderNettingPreviewColumns({
    subAccountWarning,
    actionColumn: readOnly
      ? undefined
      : {
          type: 'remove',
          onClick: removeCareOrder,
        },
  });
  const subAccountWarningTooltip = subAccountWarning?.tooltip;
  const { dataObservable, errorObservable } = useBlotterObservables({
    data: memberCareOrders,
  });
  const blotterSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<SelectableCareOrder>({
    dataObservable,
    errorObservable,
    rowID: 'OrderID' satisfies keyof SelectableCareOrder,
    columns,
    /* We add and remove rows from this blotter based on user actions. Scroll got messed up with 
    async transactions applying the state update. Going to synchronous updates fixed it. */
    applyTransactionsSynchronously: true,
    gridOptions: {
      rowSelection: readOnly ? readOnlyMemberCareOrdersRowSelection : memberCareOrdersRowSelection,
      onSelectionChanged: readOnly ? undefined : handleSelectionChanged,
    },
    settings: blotterSettings,
  });

  return (
    <Box h="100%" data-testid="care-order-netting-preview-member-care-orders">
      <BlotterTableFilters
        portalId={portalId}
        {...blotterTable.blotterTableFiltersProps}
        showFilterBuilder={false}
        showDateRangePicker={false}
        quickFilterPrefix={
          subAccountWarningTooltip == null ? undefined : (
            <Tooltip tooltip={subAccountWarningTooltip} tooltipTestID="care-order-netting-sub-account-warning-tooltip">
              <HStack fontSize="fontSizeSm" gap="spacing4" data-testid="care-order-netting-sub-account-warning">
                <Text color="colorTextImportant">Differing sub accounts</Text>
                <Icon icon={IconName.ExclamationSolid} color="colorTextWarning" />
              </HStack>
            </Tooltip>
          )
        }
        suffix={
          readOnly ? undefined : (
            <HStack gap="spacing6">
              <Button
                size={FormControlSizes.Small}
                onClick={removeSelectedCareOrders}
                startIcon={IconName.Close}
                disabled={selectedRows.length === 0}
                data-testid="care-order-netting-remove-selected-button"
              >
                Remove Selected
              </Button>
              {!showCompatibleCareOrders && (
                <Button
                  size={FormControlSizes.Small}
                  onClick={onShowCompatibleCareOrders}
                  data-testid="care-order-netting-show-compatible-care-orders-button"
                  startIcon={IconName.Plus}
                >
                  More compatible orders
                </Button>
              )}
            </HStack>
          )
        }
      />
      <Box h="100%">
        <BlotterTable {...blotterTable} />
      </Box>
    </Box>
  );
}
